import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel, Alert, IconButton,
  CircularProgress, TableContainer
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../services/api';
import Layout from '../../components/Layout';

const statusColor = { trial: 'info', active: 'success', past_due: 'warning', suspended: 'error', cancelled: 'default' };
const acctStatusColor = { pending_verification: 'warning', pending: 'warning', approved: 'success', suspended: 'error' };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/plans'),
      ]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setPlans(Array.isArray(p.data) ? p.data : []);
    } catch (e) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      role: u.role,
      account_status: u.status || '',
      plan_id: u.plan_id || '',
      sub_status: u.sub_status || 'trial',
      trial_ends_at: u.trial_ends_at ? u.trial_ends_at.slice(0, 10) : '',
      contract_limit_override: u.contract_limit_override ?? '',
      admin_notes: u.admin_notes || '',
    });
  };

  const save = async () => {
    if (!editUser) return;
    setSaving(true); setError('');
    try {
      // Update role first if changed
      if (editForm.role !== editUser.role) {
        await api.put(`/admin/users/${editUser.id}/role`, { role: editForm.role });
      }
      // Update account status if changed
      if (editForm.account_status && editForm.account_status !== editUser.status) {
        await api.put(`/admin/users/${editUser.id}/account-status`, { status: editForm.account_status });
      }
      // Update subscription if user is/becomes a sub
      if (editForm.role === 'subcontractor' || editUser.plan_id) {
        const body = {};
        if (editForm.plan_id) body.plan_id = editForm.plan_id;
        if (editForm.sub_status) body.status = editForm.sub_status;
        if (editForm.trial_ends_at) body.trial_ends_at = editForm.trial_ends_at;
        if (editForm.contract_limit_override !== '') body.contract_limit_override = editForm.contract_limit_override === null ? null : Number(editForm.contract_limit_override);
        if (editForm.admin_notes !== editUser.admin_notes) body.admin_notes = editForm.admin_notes;
        if (Object.keys(body).length > 0) {
          await api.put(`/admin/users/${editUser.id}/subscription`, body);
        }
      }
      setEditUser(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>Users</Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          All Yaplar users — manage roles, subscriptions, trial extensions, suspensions.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <CircularProgress /> : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f6fa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Email / Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Usage</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Trial Ends</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(u => {
                  const limit = u.contract_limit_override ?? u.max_active_contracts;
                  const overLimit = limit && u.active_contracts >= limit;
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.email}</Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>{u.first_name} {u.last_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={u.role} sx={{ textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell>
                        {u.status ? (
                          <Chip size="small" color={acctStatusColor[u.status] || 'default'} label={(u.status || '').replace('_', ' ')} sx={{ textTransform: 'capitalize' }} />
                        ) : <Typography variant="caption" sx={{ color: '#aaa' }}>—</Typography>}
                      </TableCell>
                      <TableCell>
                        {u.plan_name ? (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.plan_name}</Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>${(u.monthly_price_cents / 100).toFixed(0)}/mo</Typography>
                          </>
                        ) : <Typography variant="caption" sx={{ color: '#aaa' }}>—</Typography>}
                      </TableCell>
                      <TableCell>
                        {u.sub_status ? (
                          <Chip size="small" color={statusColor[u.sub_status] || 'default'} label={u.sub_status} />
                        ) : <Typography variant="caption" sx={{ color: '#aaa' }}>—</Typography>}
                      </TableCell>
                      <TableCell>
                        {u.role === 'subcontractor' ? (
                          <Typography variant="body2" sx={{ color: overLimit ? '#c62828' : 'inherit', fontWeight: overLimit ? 700 : 500 }}>
                            {u.active_contracts} / {limit ?? '∞'}
                          </Typography>
                        ) : <Typography variant="caption" sx={{ color: '#aaa' }}>—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {u.trial_ends_at ? new Date(u.trial_ends_at).toLocaleDateString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(u)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={!!editUser} onClose={() => !saving && setEditUser(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e' }}>Edit user</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editUser && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                {editUser.email}
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select value={editForm.role || ''} label="Role" onChange={e => setEditForm({...editForm, role: e.target.value})}>
                  <MenuItem value="homeowner">Homeowner</MenuItem>
                  <MenuItem value="subcontractor">Subcontractor</MenuItem>
                  <MenuItem value="general_contractor">General Contractor</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Account Status</InputLabel>
                <Select value={editForm.account_status || ''} label="Account Status" onChange={e => setEditForm({...editForm, account_status: e.target.value})}>
                  <MenuItem value="pending_verification">Pending email verification</MenuItem>
                  <MenuItem value="pending">Pending admin approval</MenuItem>
                  <MenuItem value="approved">Approved (can sign in)</MenuItem>
                  <MenuItem value="suspended">Suspended (blocked)</MenuItem>
                </Select>
              </FormControl>

              {(editForm.role === 'subcontractor' || editUser.plan_id) && (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Plan</InputLabel>
                    <Select value={editForm.plan_id || ''} label="Plan" onChange={e => setEditForm({...editForm, plan_id: e.target.value})}>
                      {plans.map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.name} — ${(p.monthly_price_cents/100).toFixed(0)}/mo</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Subscription status</InputLabel>
                    <Select value={editForm.sub_status || ''} label="Subscription status" onChange={e => setEditForm({...editForm, sub_status: e.target.value})}>
                      <MenuItem value="trial">Trial</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="past_due">Past due</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField label="Trial ends at" type="date" fullWidth size="small" sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: true }}
                    value={editForm.trial_ends_at || ''}
                    onChange={e => setEditForm({...editForm, trial_ends_at: e.target.value})}
                  />
                  <TextField label="Contract limit override (leave blank = use plan default)" type="number" fullWidth size="small" sx={{ mb: 2 }}
                    value={editForm.contract_limit_override}
                    onChange={e => setEditForm({...editForm, contract_limit_override: e.target.value})}
                  />
                  <TextField label="Admin notes (internal)" multiline rows={2} fullWidth size="small" sx={{ mb: 1 }}
                    value={editForm.admin_notes || ''}
                    onChange={e => setEditForm({...editForm, admin_notes: e.target.value})}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditUser(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor: '#1a237e' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
